import { useState, useEffect } from "react";

interface UseExitIntentOptions {
  /**
   * localStorage key to track if exit intent has been triggered
   */
  storageKey?: string;
  /**
   * Whether to only trigger once per session (uses localStorage)
   * @default true
   */
  triggerOnce?: boolean;
  /**
   * Whether the hook is enabled
   * @default true
   */
  enabled?: boolean;
}

interface UseExitIntentReturn {
  /**
   * Whether the exit intent has been triggered
   */
  isTriggered: boolean;
  /**
   * Reset the triggered state (allows exit intent to trigger again)
   */
  reset: () => void;
  /**
   * Dismiss the exit intent (marks as triggered and persists to localStorage if triggerOnce is true)
   */
  dismiss: () => void;
}

export function useExitIntent(
  options: UseExitIntentOptions = {}
): UseExitIntentReturn {
  const {
    storageKey = "exitIntentDismissed",
    triggerOnce = true,
    enabled = true,
  } = options;

  const [isTriggered, setIsTriggered] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    if (triggerOnce && typeof window !== "undefined") {
      const dismissed = localStorage.getItem(storageKey);
      if (dismissed === "true") {
        setIsDismissed(true);
      }
    }
  }, [storageKey, triggerOnce]);

  // Set up exit intent listener
  useEffect(() => {
    if (!enabled || isDismissed) return;

    const handleMouseLeave = () => {
      setIsTriggered(true);
    };

    document.documentElement.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.documentElement.removeEventListener(
        "mouseleave",
        handleMouseLeave
      );
    };
  }, [enabled, isDismissed]);

  const reset = () => {
    setIsTriggered(false);
  };

  const dismiss = () => {
    setIsTriggered(false);
    setIsDismissed(true);
    if (triggerOnce && typeof window !== "undefined") {
      localStorage.setItem(storageKey, "true");
    }
  };

  return {
    isTriggered,
    reset,
    dismiss,
  };
}
